import { Injectable } from '@angular/core';
import { OrderQuery } from '@modules/order/state/order-query';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { UnitTypes } from '../models';
import { fromUnitType, fromUnitTypeInBridge, IntuitiveIndication, toUnitTypeOrDefault } from '../models/intuitive-indication';
import { isDentureRemovableFullDentureImplantBased } from '@shared/utils/procedure-type-util';

/**
 * In application code prefer using
 * {@link RxIntuitiveIndicationRulesService#getDefaultIndication} and
 * {@link RxIntuitiveIndicationRulesService#getDefaultUnitType}
 * Do not import these constants anywhere except test mocks.
 */
export const DEFAULT_INDICATION = IntuitiveIndication.Regular;
export const DEFAULT_UNIT_TYPE = UnitTypes.Regular;

@Injectable()
export class RxIntuitiveIndicationMapperService {
	constructor(private readonly orderQuery: OrderQuery) {}
	getDefaultUnitType(): UnitTypes {
		return toUnitTypeOrDefault(this.getDefaultIndication(), DEFAULT_UNIT_TYPE);
	}

	getDefaultIndication(): IntuitiveIndication {
		if (this.isDentureImplantBased()) {
			return IntuitiveIndication.Missing;
		}

		return DEFAULT_INDICATION;
	}

	getBridgeIndicationForDiagram(tooth: Tooth): IntuitiveIndication {
		if (tooth.ToothInBridgeTypeID) {
			return fromUnitTypeInBridge(tooth.ToothInBridgeTypeID) ?? DEFAULT_INDICATION;
		} else {
			return DEFAULT_INDICATION;
		}
	}

	getIndicationForDiagram(indication: IntuitiveIndication | null | undefined): IntuitiveIndication {
		if (!indication || indication === DEFAULT_INDICATION) {
			return this.getDefaultIndication();
		}

		return indication ?? this.getDefaultIndication();
	}

	getUnitTypeIndicationForDiagram(unitType: UnitTypes | null | undefined): IntuitiveIndication {
		return this.getIndicationForDiagram(fromUnitType(unitType));
	}

	isDefaultUnitType(unitType: UnitTypes | null | undefined): boolean {
		return this.getUnitTypeIndicationForDiagram(unitType) === this.getDefaultIndication();
	}

	isDefaultIndication(indication: IntuitiveIndication | null | undefined): boolean {
		return this.getIndicationForDiagram(indication) === this.getDefaultIndication();
	}

	private isDentureImplantBased(): boolean {
		const procedureId = this.orderQuery.procedure?.Id;
		const procedureMapTypeId = this.orderQuery.procedureMap?.TypeId;

		return isDentureRemovableFullDentureImplantBased(procedureId, procedureMapTypeId);
	}
}
